import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { twMerge } from "tailwind-merge";

import { ReactComponent as InfoIcon } from "@/assets/icons/info-icon.svg";
import { Colors } from "@/constants/colors";
import { EventLinkTypes } from "@/constants/event";
import { ExperimentIds } from "@/constants/experiments";
import useExternalLinkWithAnalytics from "@/hooks/useExternalLinkWithAnalytics";
import i18n from "@/localization/i18next";
import { useExperiment } from "@/utils/ExperimentsProvider";

import CheckboxConsent from "./CheckboxConsent";

interface useUserDataConsentProps {
    onConsent?: () => void;
    event: string;
    withScrollToConsent?: boolean;
}
interface ConsentProps {
    canProceed: boolean;
    className?: string;
    withScrollToConsent?: boolean;
    inputClass?: string;
}
const useUserDataConsent = ({ onConsent, event, withScrollToConsent }: useUserDataConsentProps) => {
    const [isPolicyConsentChecked, setIsPolicyConsentChecked] = useState(false);

    const handlePolicyConsentClick = () => {
        setIsPolicyConsentChecked(!isPolicyConsentChecked);
        onConsent?.();
    };

    return {
        isConsent: isPolicyConsentChecked,
        Component: (props: ConsentProps) => (
            <UserDataConsent
                checked={isPolicyConsentChecked}
                onChange={handlePolicyConsentClick}
                event={event}
                withScrollToConsent={withScrollToConsent}
                {...props}
            />
        ),
    };
};
interface UserDataConsentProps extends ConsentProps {
    onChange: () => void;
    checked: boolean;
    event: string;
}

const UserDataConsent = ({
    onChange,
    checked,
    event,
    canProceed,
    className,
    withScrollToConsent,
    inputClass,
}: UserDataConsentProps) => {
    const { handleExternalLinkClick } = useExternalLinkWithAnalytics(event);
    const alertRef = useRef<HTMLDivElement>(null);
    const consentRef = useRef<HTMLDivElement>(null);

    const { isTest: isNewQsSequenceExp } = useExperiment({
        experimentId: ExperimentIds.NEW_QS_SEQUENCE,
    });

    useEffect(() => {
        const consentInfoIsShown = canProceed && !checked;
        if ((withScrollToConsent || isNewQsSequenceExp) && consentInfoIsShown) {
            if (inputClass) {
                const inputs = Array.from(
                    document.getElementsByClassName(
                        inputClass
                    ) as HTMLCollectionOf<HTMLInputElement>
                );
                inputs.forEach((input) => {
                    input.blur();
                });
            }
        }
    }, [isNewQsSequenceExp, inputClass, canProceed, checked, withScrollToConsent]);

    return (
        <div className={twMerge("grid gap-4", className)} ref={consentRef}>
            <CheckboxConsent
                checked={checked}
                onChange={onChange}
                label={
                    <div>
                        <Trans i18nKey="privacy_policy_consent">
                            <a
                                className="text-white underline female:text-black"
                                onClick={handleExternalLinkClick(EventLinkTypes.privacyPolicy)}
                            />
                        </Trans>
                    </div>
                }
            />
            <div
                ref={alertRef}
                className={classNames(
                    "flex animate-errorBounce items-center gap-2 rounded bg-zing-warm-neon-green-500 px-4 py-2.5 text-zing-12-18 text-black female:bg-zing-warm-yellow-200",
                    { hidden: !canProceed || checked }
                )}
            >
                <InfoIcon color={Colors.BLACK} />
                <span>{i18n.t("accept_privacy_policy")}</span>
            </div>
        </div>
    );
};

export default useUserDataConsent;
